import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { getHotKey } from "@v1/utils/getHotKey";

type InputSize = "sm" | "base";

type InputOwnProps = {
  value?: string | number;
  defaultValue?: string | number;
  size?: InputSize;
  type?: string;
  placeholder?: string | null;
  autoFocus?: boolean;
  autoComplete?: string;
  startEnhancer?: React.ReactNode;
  endEnhancer?(): React.ReactNode;
  isDisabled?: boolean;
  inputContainerRef?: React.Ref<HTMLDivElement>;
  isReadOnly?: boolean;
  // NOTE (Chance 2023-11-13): Similar to `isPhonyButton` in `Button`, this prop
  // renders a container `div` that is styled like an input but does not accept
  // user input or hold a value.
  isPhonyInput?: boolean;
  validityState?: "valid" | "invalid";
  name?: string;
  id?: string;
  onChange?(e: React.ChangeEvent<HTMLInputElement>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement>): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement>): void;
  onEnter?(): void;
  onEscape?(): void;
  onKeyDown?(e: React.KeyboardEvent<HTMLInputElement>): void;
  onKeyUp?(e: React.KeyboardEvent<HTMLInputElement>): void;
  onKeyPress?(e: React.KeyboardEvent<HTMLInputElement>): void;
  onMouseDown?(e: React.MouseEvent<HTMLInputElement>): void;
  onMouseUp?(e: React.MouseEvent<HTMLInputElement>): void;
  onMouseMove?(e: React.MouseEvent<HTMLInputElement>): void;
  onClick?(e: React.MouseEvent<HTMLInputElement>): void;
  onPaste?(e: React.ClipboardEvent<HTMLInputElement>): void;
  layoutClassName?: string;
  /**
   * **IMPORTANT** (Chance 2023-11-10): Adding classnames to design system
   * components should be considered an escape hatch and avoided if possible.
   * Things like spacing and layout can generally be handled with wrapper layout
   * components. Design variations should be considered in the design system.
   */
  unsafe_className?: string;
  /**
   * **IMPORTANT** (Chance 2023-11-10): Adding classnames to design system
   * components should be considered an escape hatch and avoided if possible.
   * Things like spacing and layout can generally be handled with wrapper layout
   * components. Design variations should be considered in the design system.
   */
  unsafe_inputClassName?: string;
  /**
   * **IMPORTANT** (Chance 2023-11-10): Adding classnames to design system
   * components should be considered an escape hatch and avoided if possible.
   * Things like spacing and layout can generally be handled with wrapper layout
   * components. Design variations should be considered in the design system.
   */
  unsafe_style?: React.CSSProperties;
  allowsDragOnStartEnhancer?: boolean;
  isFocused?: boolean;
  isRequired?: boolean;
  min?: string | number;
  max?: string | number;
  maxLength?: number;
  shouldSelectTextOnFocus?: boolean;
  onOptionClick?(): void;
  startEnhancerClassName?: string;
};

type InputProps = InputOwnProps &
  Omit<
    React.ComponentPropsWithRef<"input">,
    keyof InputOwnProps | "className" | "style"
  >;

/**
 * @deprecated Use Input from @replo/design-system/components/input/Input instead.
 *
 * Migration notes:
 * - isDisabled -> disabled
 * - isReadOnly -> readOnly
 * - validityState="invalid" -> invalid={true}
 * - layoutClassName -> className
 * - unsafe_className -> UNSAFE_className
 * - onEnter/onEscape -> implement via onKeyDown
 * - endEnhancer={() => <X />} -> endEnhancer={<X />}
 */
const DeprecatedInput = React.forwardRef<HTMLInputElement, InputProps>(
  function DeprecatedInput(
    {
      placeholder,
      size = "sm",
      type = "text",
      autoFocus = false,
      autoComplete = "on",
      startEnhancer,
      endEnhancer,
      isDisabled = false,
      validityState,
      isReadOnly,
      isPhonyInput,
      id,
      inputContainerRef,
      onBlur,
      onFocus,
      onEnter,
      onEscape,
      onKeyPress,
      onMouseDown,
      onMouseUp,
      onMouseMove,
      unsafe_className: className,
      layoutClassName,
      unsafe_inputClassName: inputClassName,
      allowsDragOnStartEnhancer = false,
      unsafe_style: style,
      // TODO (Noah, Chance, REPL-9830): We need to fix the controlledFocus
      // of these inputs, currently it's broken
      isFocused: _controlledFocus = false,
      isRequired = false,
      startEnhancerClassName,
      shouldSelectTextOnFocus = false,
      onOptionClick,
      ...props
    }: InputOwnProps,
    forwardedRef,
  ) {
    const inputClassNames = twMerge(
      "bg-inherit text-default truncate border-0 rounded-sm w-full focus:outline-hidden h-full",
      "placeholder:text-placeholder placeholder:font-normal",
      "disabled:cursor-not-allowed",
      "focus-visible:ring-0",
      size === "sm" && "text-xs leading-6",
      size === "base" && "text-sm leading-8",
      layoutClassName,
      inputClassName,
    );

    const enhancerSizeClasses = {
      sm: "text-xs",
      base: "text-sm",
    }[size];

    const enhancerClassNames = twMerge(
      "flex items-center text-muted",
      enhancerSizeClasses,
    );

    function onKeyPressInput(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === "Enter" && onEnter) {
        onEnter?.();
      }
      if (e.key === "Escape" && onEscape) {
        onEscape?.();
      }
      return onKeyPress?.(e);
    }

    const shouldAddMouseHandlers =
      !Boolean(startEnhancer) || !allowsDragOnStartEnhancer;

    const onInputMouseUp = (
      e: React.MouseEvent<HTMLInputElement, globalThis.MouseEvent>,
    ) => {
      onMouseUp?.(e);
      if (onOptionClick) {
        const hotkey = getHotKey(e);
        if (hotkey === "altKey") {
          onOptionClick();
        }
      }
    };
    const endEnhancerContent = endEnhancer?.();

    return (
      <div
        className={twMerge(
          "bg-sheet justify-content-around text-default border-0.5 border-component-border relative flex items-center rounded-md px-[1px] transition-all duration-75 focus-within:border-transparent",
          isDisabled && "cursor-not-allowed opacity-50",
          size === "sm" && "h-6",
          size === "base" && "h-8 max-sm:h-10",
          size === "base" && endEnhancerContent && "h-10",
          validityState === "invalid" && "border-transparent",
          layoutClassName,
          className,
        )}
        ref={inputContainerRef}
        // NOTE (Sebas, 2024-06-27): We need to stop propagation of the keydown event
        // to prevent the hotkeys from being triggered.
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div
          className={twMerge(
            "justify-content-around relative flex h-full flex-1 grow items-center rounded-md transition-all duration-75",
            validityState !== "invalid" &&
              "focus-within:ring-primary focus-within:ring-1 focus-within:ring-inset",
            validityState === "invalid" && "bg-rose-soft ring-rose ring-1",
            size === "sm" && "gap-1 px-1.5",
            size === "base" && "gap-2 px-2",
            layoutClassName,
            className,
          )}
        >
          {startEnhancer && (
            <div
              className={twMerge(
                enhancerClassNames,
                "pr-0",
                startEnhancerClassName,
              )}
              style={{
                cursor: allowsDragOnStartEnhancer ? "ns-resize" : style?.cursor,
              }}
              onBlur={onBlur}
              onFocus={onFocus}
            >
              {startEnhancer}
            </div>
          )}
          {isPhonyInput ? (
            <div className={inputClassNames} style={style} id={id}></div>
          ) : (
            <input
              {...props}
              className={inputClassNames}
              style={style}
              type={type}
              placeholder={placeholder ?? undefined}
              autoFocus={autoFocus}
              autoComplete={autoComplete}
              disabled={isDisabled}
              readOnly={isReadOnly}
              onBlur={onBlur}
              onFocus={(event) => {
                onFocus?.(event);
                if (!event.defaultPrevented && shouldSelectTextOnFocus) {
                  event.target.select();
                }
              }}
              onKeyPress={onKeyPressInput}
              onMouseDown={shouldAddMouseHandlers ? onMouseDown : undefined}
              onMouseUp={shouldAddMouseHandlers ? onInputMouseUp : undefined}
              onMouseMove={shouldAddMouseHandlers ? onMouseMove : undefined}
              ref={forwardedRef}
              id={id}
              required={isRequired}
            />
          )}
          {endEnhancerContent}
        </div>
      </div>
    );
  },
);

export default DeprecatedInput;

/**
 * @deprecated Use Input from @replo/design-system/components/input/Input with
 * useDebouncedState from replo-utils/react/use-debounced-state instead.
 */
export function DebouncedInput({
  value,
  onValueChange,
  ...props
}: Omit<InputProps, "defaultValue" | "onChange"> & {
  value: string;
  onValueChange(value: string): any;
}) {
  const inputProps = useOverridableInput({ value, onValueChange });
  return <DeprecatedInput {...props} {...inputProps} />;
}
